import api from '../api';
import Store from '../store';
/**
 * @typedef UserCreateInfo
 * @type {Object}
 * @property {String} message - display message for result.
 * @property {Boolean} successful - successfully created user
 * @property {String} userId - id of newly create user
 */

/**
 * @param {String} d the date to format
 * @return {Date} the date for the input
 */
const formatDate = (d) => {
  const parts = d.split('/');
  return `${parts[1]}/${parts[0]}/${parts[2]}`;
};

/**
 * @param {Object} payload
 * @returns {Object} copy of payload object with nulled empty string fields
 */
const setEmptyToNull = (payload) => {
  const copy = {};
  Object.entries(payload).forEach((kvp) => {
    const [k, v] = kvp;
    const nonFields = ['degreeFile', 'cvFile'];

    if (!nonFields.includes(k)) {
      copy[k] = v === '' ? null : v;
    }
  });

  return copy;
};

const createUserInfo = function (userId) {
  const userCreate = {
    UserId: userId,
    EntraExternalId: null,
  };
  ({
    title: userCreate.Title,
    email: userCreate.Email,
    firstname: userCreate.FirstName,
    lastname: userCreate.LastName,
    password: userCreate.Password,
    dob: userCreate.DateOfBirth,
  } = userId ? Store.state.application.PersonalDetails : Store.state.personalDetailsJson);

  if (userCreate.DateOfBirth) {
    userCreate.DateOfBirth = formatDate(userCreate.DateOfBirth);
  }

  return userCreate;
};

export default {
  /** @description save personal details, create user then save membership data
   * @returns {UserCreateInfo} details of new user
   */
  async savePersonalDetails() {
    const userCreate = createUserInfo(null);

    try {
      const response = await api.saveUser(userCreate);
      const userCreateInfo = response.data;

      if (!userCreateInfo.successful) {
        return userCreateInfo;
      }

      if (process.env.NODE_ENV === 'development') {
        window.sessionStorage.setItem('dnn-user', JSON.stringify({
          userId: userCreateInfo.userId,
          email: userCreate.Email,
        }));
      }

      userCreate.UserId = userCreateInfo.userId;
      userCreate.EntraExternalId = userCreateInfo.EntraExternalId;

      // try catch so remove the dnn user if salesforce creation is unsuccessful
      try {
        await this.createApplication(userCreate);
        return userCreateInfo;
      } catch (e) {
        await api.deleteDnnUser(userCreateInfo.userId, userCreateInfo.EntraExternalId);
        console.log(`User ${userCreateInfo.userId} is now deleted as ${e} error occured because salesforce member creation failed`);
        return {
          message: 'There has been a problem creating your user. Please contact the Membership Team on 0333 733 2570 so they can assist you.',
          userId: '',
          successful: false,
        };
      }
    } catch (e) {
      console.log(e);
      return {
        message: 'There has been a problem creating your application. Please contact the Membership Team on 0333 733 2570 so they can assist you.',
        userId: '',
        successful: false,
      };
    }
  },
  async updatePersonDetails(personDetails) {
    const update = async (details) => {
      const response = await api.updateUser(details);
      const userUpdateInfo = response.data;

      return userUpdateInfo;
    };
    const updatedPersonDetails = {
      ...personDetails,
      dateOfBirth: formatDate(personDetails.dob),
    };
    try {
      return await update(updatedPersonDetails);
    } catch (e) {
      console.log(e);
      // HACK if method not allowed try once more as unauthenticated user (username change has dnoe away with auth session cookie)
      if (e.response.status === 405) {
        const result = await update(updatedPersonDetails);
        return result;
      }

      return {
        message: 'There has been a problem updating your application. Please contact the Membership Team on 0333 733 2570 so they can assist you.',
        userId: '',
        successful: false,
      };
    }
  },
  async createApplication(user) {
    const userData = typeof user === 'number' ? createUserInfo(user) : user;

    if (Store.state.application.Type === 'Pharmaceutical Scientist') {
      if (Store.state.application.UnderstandingYou[Store.state.application.Type].degreeFile) {
        const degreeLocations = await api.uploadFilesForUser(
          userData.UserId,
          true,
          [Store.state.application.UnderstandingYou[Store.state.application.Type].degreeFile],
        );
        Store.state.application.UnderstandingYou[Store.state.application.Type].degreesDoc = degreeLocations.data.file;
      }

      if (Store.state.application.UnderstandingYou[Store.state.application.Type].cvFile) {
        const cvLocations = await api.uploadFilesForUser(
          userData.UserId,
          false,
          [Store.state.application.UnderstandingYou[Store.state.application.Type].cvFile],
        );
        Store.state.application.UnderstandingYou[Store.state.application.Type].cvDoc = cvLocations.data.file;
      }
    }

    const result = await api.createApplication({
      MembershipType: Store.state.application.Type,
      User: userData,
      MemberData: JSON.stringify(setEmptyToNull(Store.state.application.UnderstandingYou[Store.state.application.Type])),
      EntraExternalId: user.EntraExternalId,
    });

    Store.commit('addOpportunity', result.data);
  },
  async setPaymentOption(option) {
    const selectedPaymentOption = {
      opportunityId: Store.state.application.OpportunityId,
      pricebookentryId: option.PriceBookEntryId,
      quantity: 1,
      unitPrice: option.Amount,
    };

    await api.postFee(selectedPaymentOption);
  },
  async saveCorrespondenceDetails() {
    try {
      const result = await api.saveCorrespondence(Store.state.application.Correspondence);
      if (result.status === 200) {
        Store.commit('setOverseasStatus', result.data);
        return {
          message: 'Correspondence details have been saved.',
          successful: true,
          reloadForm: result.data.reloadForm,
        };
      }
      return result;
    } catch (e) {
      console.log(e);
      return {
        message: 'There has been a problem saving your correspondence details. Please contact the'
          + ' Membership Team on 0333 733 2570 so they can assist you.',
        userId: '',
        successful: false,
      };
    }
  },
  async saveInsuranceDetails() {
    try {
      const result = await api.savePliData(Store.state.application.Insurance);
      if (result.status === 200) {
        return {
          message: 'Insurance details have been saved.',
          successful: true,
          reloadForm: result.data.reloadForm,
        };
      }
      return result;
    } catch (e) {
      console.log(e);
      return {
        message: 'There has been a problem saving your insurance details. Please contact the'
          + ' Membership Team on 0333 733 2570 so they can assist you.',
        userId: '',
        successful: false,
        reloadForm: true,
      };
    }
  },
  async getCurrentApplication() {
    try {
      return (await api.getApplication()).data;
    } catch (e) {
      console.log(e);
      return {
        application: {},
        isFound: false,
      };
    }
  },
  async getCurrentFees() {
    try {
      return (await api.getFees()).data;
    } catch (e) {
      console.log(e);
      return {
        fees: null,
        message: 'There has been an issue retrieving the fees. Please contact the Membership Team on 0333 733 2570 so they can assist you.',
        successful: false,
      };
    }
  },
  async getCountryList() {
    try {
      return (await api.getCountryList()).data;
    } catch (e) {
      console.log(e);
      return {
        message: 'There has been an issue retrieving the countries',
        successful: false,
      };
    }
  },
  /**
   * This function calls GA to send card payment
   */
  async postCardComplete(worldPayResult) {
    try {
      const application = 0;
      const result = await api.postCardComplete(worldPayResult, application);
      return result.data;
    } catch (e) {
      console.log(e);
      return null;
    }
  },
  async postCardCompleteBraintree(braintreeResult) {
    try {
      const application = 0;
      const result = await api.postCardCompleteBraintree(braintreeResult, application);
      return result.data;
    } catch (e) {
      console.log(e);
      return null;
    }
  },
  async postApplicationComplete() {
    try {
      await api.completeApplication();
      window?.lintrk('track', { conversion_id: 14318762 });
    } catch (e) {
      console.log(e);
    }
  },
};
